import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Download, Trash2, Lock, Loader2, Shield, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";

import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { useStealthToggle } from "../hooks/useStealthToggle";
import { exportMyData, deleteMyAccount } from "../lib/api";
import { stealthWipeAll } from "../lib/stealthDb";
import VoiceCoachPreview from "../components/VoiceCoachPreview";
import ToneProfileCard from "../components/ToneProfileCard";
import AllergenProfileCard from "../components/AllergenProfileCard";
import SkinProfileCard from "../components/SkinProfileCard";
import StylePreferencesCard from "../components/StylePreferencesCard";
import BeautyArsenalCard from "../components/BeautyArsenalCard";
import BrandAffinityCard from "../components/BrandAffinityCard";
import LockedBrandsCard from "../components/LockedBrandsCard";
import RedeemCard from "../components/RedeemCard";
import LanguageSelectorCard from "../components/LanguageSelectorCard";
import MemberBadges from "../components/MemberBadges";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import ShadowZoneProfileCard from "../components/ShadowZoneProfileCard";
import DemoControlsCard from "../components/DemoControlsCard";

const ConfirmDelete = ({ open, onCancel, onConfirm, busy }) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  if (!open) return null;
  const safe = text.trim().toLowerCase() === t("settings.modal.confirmKeyword");
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2123]/60 backdrop-blur-sm px-4"
      data-testid="confirm-delete-modal"
    >
      <div className="bg-white rounded-3xl p-7 max-w-md w-full soft-shadow-lg">
        <div className="h-11 w-11 rounded-full bg-[#F8EEEE] flex items-center justify-center mb-4">
          <Trash2 className="h-5 w-5 text-[#9B6A6C]" />
        </div>
        <h2 className="text-2xl font-light text-[#2C2123] mb-2">
          {t("settings.modal.title")}
        </h2>
        <p className="text-[#5C4B51] mb-5">
          <Trans
            i18nKey="settings.modal.body"
            components={{
              kw: (
                <span className="font-mono text-[#5D4050] bg-[#F8EEEE] px-1.5 py-0.5 rounded" />
              ),
            }}
          />
        </p>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("settings.modal.placeholder")}
          className="w-full border border-[#EADDD7] rounded-xl px-4 py-3 mb-5 focus:outline-none focus:border-[#9B6A6C] bg-white"
          data-testid="confirm-delete-input"
        />
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-5 py-2.5 rounded-full border border-[#EADDD7] text-[#5C4B51] hover:bg-[#FAF6F2]"
            data-testid="confirm-delete-cancel"
          >
            {t("settings.modal.keepBtn")}
          </button>
          <button
            onClick={onConfirm}
            disabled={!safe || busy}
            className="px-5 py-2.5 rounded-full bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-40 flex items-center gap-2"
            data-testid="confirm-delete-confirm"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("settings.modal.confirmBtn")}
          </button>
        </div>
      </div>
    </div>
  );
};

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-6 soft-shadow">
    <div className="h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center mb-3">
      <Icon className="h-5 w-5 text-[#9B6A6C]" />
    </div>
    <h3 className="text-lg font-medium text-[#2C2123] mb-2">{title}</h3>
    <div className="text-sm text-[#5C4B51] leading-relaxed">{children}</div>
  </div>
);

const Settings = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  // Fetch usage so we can render Founding Member + Day One badges +
  // active Pro state. Settings is the canonical "your account" page.
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/usage`, {
          withCredentials: true,
        });
        if (alive) setUsage(data);
      } catch {
        /* Not signed in or transient — badges just won't render. */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  // Single source of truth for the Stealth toggle — same hook the
  // header pill uses, so the count, confirm-on-wipe, and toast copy
  // never drift between the two surfaces.
  const {
    stealth,
    counts: localCounts,
    toggle: toggleStealth,
    refreshCounts,
  } = useStealthToggle();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onToggleStealth = (next) => toggleStealth(next);

  const onWipeLocal = async () => {
    if (localCounts.routines === 0 && localCounts.snapshots === 0) return;
    const ok = window.confirm(t("settings.wipeLocalConfirm"));
    if (!ok) return;
    await stealthWipeAll();
    refreshCounts();
    toast.success(t("settings.wipeLocalToast"));
  };

  const onExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const data = await exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trubeaute-data-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("settings.exportToastSuccess"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("settings.exportToastFail"));
    } finally {
      setExporting(false);
    }
  };

  const onDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteMyAccount();
      toast.success(t("settings.deleteToastSuccess"));
      await logout?.();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("settings.deleteToastFail")
      );
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <main
        className="pt-32 pb-24 px-6 max-w-3xl mx-auto"
        data-testid="settings-page"
      >
        <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-3">
          {t("settings.kicker")}
        </span>
        <h1 className="text-4xl font-light text-[#2C2123] mb-3">
          {t("settings.headlineBefore")}{" "}
          <span className="font-serif italic text-[#5D4050]">{t("settings.headlineEmphasis")}</span>
          .
        </h1>
        <p className="text-[#5C4B51] mb-4 max-w-xl">
          {user?.email ? (
            <Trans
              i18nKey="settings.introAuthed"
              values={{ email: user.email }}
              components={{ em: <span className="text-[#5D4050]" /> }}
            />
          ) : (
            t("settings.introSignedIn")
          )}
        </p>

        {/* Member badges — Founding Member (purple Crown) for beta_allowlist
            members, Day One (gold Sparkles) for first-100 signups. */}
        {usage && (usage.is_founding_member || usage.is_day_one) && (
          <div className="mb-10" data-testid="settings-member-badges">
            <MemberBadges
              isFoundingMember={usage.is_founding_member}
              isDayOne={usage.is_day_one}
              variant="large"
            />
            {usage.is_founding_member && usage.founding_member_until && (
              <p className="text-xs text-[#8A7D82] mt-3">
                <Trans
                  i18nKey="settings.foundingNote"
                  values={{
                    date: new Date(usage.founding_member_until).toLocaleDateString(undefined, {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    }),
                  }}
                  components={{ em: <span className="text-[#5D4050]" /> }}
                />
              </p>
            )}
          </div>
        )}
        {(!usage || (!usage.is_founding_member && !usage.is_day_one)) && (
          <div className="mb-10" />
        )}

        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          <Card icon={Lock} title={t("settings.encryptedTitle")}>
            {t("settings.encryptedBody")}
          </Card>
          <Card icon={Shield} title={t("settings.neverStoreTitle")}>
            {t("settings.neverStoreBody")}
          </Card>
        </div>

        {/* Drag-mom voice preview — lets users hear the Mirror Mode TTS
            voice and pre-enable it before opening the AR mirror. */}
        <VoiceCoachPreview className="mb-10" />

        {/* ---- App language (UI + Trouvé voice + emails) ---- */}
        <LanguageSelectorCard />

        {/* ---- My tone profile — encrypted undertone/depth/coverage/finish ---- */}
        <ToneProfileCard />

        {/* ---- Allergies & sensitivities ---- */}
        <AllergenProfileCard />

        {/* ---- Skin concerns & life stage ---- */}
        <SkinProfileCard />

        {/* ---- Saved onboarding answers ---- */}
        <StylePreferencesCard />

        {/* ---- Have a gift code? — high-up so founding members find it fast ---- */}
        <RedeemCard />

        {/* ---- My Beauty Arsenal — products the user already owns ---- */}
        <BeautyArsenalCard />

        {/* ---- Brand affinity — algorithm override ---- */}
        <BrandAffinityCard />

        {/* ---- Locked brands — explicit power-user brand pin ---- */}
        <LockedBrandsCard />

        {/* ---- Custom shadow zones for the AR mirror ---- */}
        <ShadowZoneProfileCard />

        {/* ---- Founder-only demo controls (renders only for admins) ---- */}
        <DemoControlsCard />

        {/* ---- Stealth Mode ---- */}
        <div
          className={`rounded-3xl p-7 soft-shadow mb-5 transition-colors ${
            stealth
              ? "bg-[#2C2123] text-[#FAF6F2] border-2 border-[#2C2123]"
              : "bg-white/80 backdrop-blur-xl border border-[#EADDD7]"
          }`}
          data-testid="stealth-card"
        >
          <div className="flex items-start gap-5 flex-col sm:flex-row sm:items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <EyeOff
                  className={`h-4 w-4 ${stealth ? "text-[#D5A8A4]" : "text-[#9B6A6C]"}`}
                />
                <span
                  className={`text-xs uppercase tracking-[0.22em] ${
                    stealth ? "text-[#D5A8A4]" : "text-[#9B6A6C]"
                  }`}
                >
                  {t("settings.stealthCardKicker")} {stealth ? t("settings.stealthCardOn") : t("settings.stealthCardOff")}
                </span>
              </div>
              <h3
                className={`text-xl font-medium mb-2 ${stealth ? "text-white" : "text-[#2C2123]"}`}
              >
                {t("settings.stealthHeading")}
              </h3>
              <p
                className={`text-sm leading-relaxed max-w-xl ${
                  stealth ? "text-[#E5D6D2]" : "text-[#5C4B51]"
                }`}
              >
                {t("settings.stealthBody")}
              </p>
              {(localCounts.routines > 0 || localCounts.snapshots > 0) && (
                <p
                  className={`mt-3 text-xs ${stealth ? "text-[#D5A8A4]" : "text-[#8A7D82]"}`}
                  data-testid="stealth-local-counts"
                >
                  {t("settings.onThisDevice")} {localCounts.routines} {t("settings.routinesUnit")} ·{" "}
                  {localCounts.snapshots} {t("settings.snapshotsUnit")}
                </p>
              )}
            </div>
            <button
              onClick={() => onToggleStealth(!stealth)}
              className={`relative h-9 w-16 rounded-full transition-colors ${
                stealth ? "bg-[#D5A8A4]" : "bg-[#EADDD7]"
              }`}
              data-testid="stealth-toggle"
              aria-pressed={stealth}
              aria-label={t("settings.stealthToggleAria")}
            >
              <span
                className={`absolute top-1 h-7 w-7 rounded-full bg-white shadow transition-all ${
                  stealth ? "left-8" : "left-1"
                }`}
              />
            </button>
          </div>

          {stealth && localCounts.routines + localCounts.snapshots > 0 && (
            <div className="mt-5 pt-5 border-t border-[#3A2B2E] flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-[#D5A8A4]">
                {t("settings.stealthWipeFooter")}
              </p>
              <button
                onClick={onWipeLocal}
                className="text-xs text-[#FAF6F2] hover:text-white underline"
                data-testid="stealth-wipe-local-btn"
              >
                {t("settings.stealthWipe")}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-7 soft-shadow mb-5">
          <div className="flex items-start justify-between gap-5 flex-col sm:flex-row">
            <div>
              <h3 className="text-xl font-medium text-[#2C2123] mb-1">
                {t("settings.exportTitle")}
              </h3>
              <p className="text-[#5C4B51] text-sm max-w-md">
                {t("settings.exportBody")}
              </p>
            </div>
            <button
              onClick={onExport}
              disabled={exporting}
              className="rounded-full bg-[#FAF6F2] hover:bg-[#F2E8E9] border border-[#D5A8A4] text-[#5D4050] px-6 py-3 inline-flex items-center gap-2 transition disabled:opacity-60 whitespace-nowrap"
              data-testid="export-data-btn"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {t("settings.exportBtnShort")}
            </button>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl border-2 border-[#E8C7C5] rounded-3xl p-7 soft-shadow">
          <div className="flex items-start justify-between gap-5 flex-col sm:flex-row">
            <div>
              <h3 className="text-xl font-medium text-[#5D4050] mb-1">
                {t("settings.deleteTitle")}
              </h3>
              <p className="text-[#5C4B51] text-sm max-w-md">
                {t("settings.deleteBody")}
              </p>
            </div>
            <button
              onClick={() => setConfirmOpen(true)}
              className="rounded-full bg-white hover:bg-[#F8EEEE] border-2 border-[#9B6A6C] text-[#9B6A6C] px-6 py-3 inline-flex items-center gap-2 transition whitespace-nowrap"
              data-testid="open-delete-modal-btn"
            >
              <Trash2 className="h-4 w-4" />
              {t("settings.deleteBtnShort")}
            </button>
          </div>
        </div>

        <p className="text-xs text-[#8A7D82] mt-8 text-center">
          {t("settings.readFullBefore")}{" "}
          <a href="/privacy" className="text-[#9B6A6C] underline">
            {t("settings.readFullLink")}
          </a>{" "}
          {t("settings.readFullAfter")}
        </p>
      </main>

      <ConfirmDelete
        open={confirmOpen}
        busy={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onDelete}
      />
    </div>
  );
};

export default Settings;
